import {
  BearingEnvelope,
  Line2D as Line2DInterface,
  Segment2D as LineSegment2DInterface,
} from '@paragon/contract-library';
import {
  Line2D,
  Line2Struct,
  LineSegment2D,
  Point2D,
  segmentToLine,
  vec2DistanceToLineSquared,
  vec2DistanceToSegmentSquared,
  Vec2Struct,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export const CORE_ENTITY_SNAP_TARGET_TYPES = [
  'roof',
  'ceiling',
  'truss-envelope',
  'structural-wall',
  'non-structural-wall',
  'beam-bearing-envelope',
  'work-line',
  'uploaded-line',
  'feature-pattern-container-edge',
] as const;

export type EntitySnapTargetType =
  | (typeof CORE_ENTITY_SNAP_TARGET_TYPES)[number]

  // Feature pattern containers are the only type of object that can be a snapping target while being inserted. Because
  //   this is such a special case, we give the insertion container its own special type. Feel free to change this in
  //   the future if more objects need to be snapped to while being inserted.
  | 'insertion-feature-pattern-container-edge';

export function isBearingEnvelopeSnapType(type: EntitySnapTargetType): boolean {
  return type === 'structural-wall' || type === 'non-structural-wall' || type === 'beam-bearing-envelope';
}

export function getBearingEnvelopeSnapType(envelope: BearingEnvelope): EntitySnapTargetType {
  return envelope.bearingType === 'Beam'
    ? 'beam-bearing-envelope'
    : envelope.nonStructural
      ? 'non-structural-wall'
      : 'structural-wall';
}

export interface SerializedEntitySnapTarget {
  type: EntitySnapTargetType;
  originObjectGuid: string | null;
  definition: Line2DInterface | LineSegment2DInterface;
}

export class EntitySnapTarget {
  type: EntitySnapTargetType;

  // The guid of the object from which this entity snap target originated. For overhang segments and ridge lines it is
  //   the roof container guid. Not guaranteed to be present, as many pickable lines in the scene are coming from
  //   computed geometries that are not identifiable.
  originObjectGuid: string | null;

  // The geometry object which defines this entity snap target.
  definition: Line2D | LineSegment2D;

  constructor(type: EntitySnapTargetType, originObjectGuid: string | null, definition: Line2D | LineSegment2D) {
    this.type = type;
    this.originObjectGuid = originObjectGuid;
    this.definition = definition;
  }

  static fromObject(obj: SerializedEntitySnapTarget): EntitySnapTarget {
    return new EntitySnapTarget(
      obj.type,
      obj.originObjectGuid,
      'direction' in obj.definition
        ? Line2D.fromObject(obj.definition)
        : LineSegment2D.fromLineSegmentObject(obj.definition),
    );
  }

  isOfType(type: EntitySnapTargetType): boolean {
    return this.type === type;
  }

  isSegment(): boolean {
    return this.definition instanceof LineSegment2D;
  }

  getDefiningLine(): Line2D {
    return this.definition instanceof Line2D ? this.definition : this.definition.toLine();
  }

  getDefiningLineNoAlloc(result: Line2Struct) {
    if (this.definition instanceof Line2D) {
      result.basePoint.x = this.definition.basePoint.x;
      result.basePoint.y = this.definition.basePoint.y;
      result.direction.x = this.definition.direction.x;
      result.direction.y = this.definition.direction.y;
    } else {
      segmentToLine(this.definition, result);
    }
  }

  getDefiningPoints(): [Point2D, Point2D] {
    return this.definition instanceof Line2D
      ? [this.definition.basePoint, this.definition.getPointAlongLine(100)]
      : [this.definition.basePoint, this.definition.endPoint];
  }

  distanceFrom(point: Point2D): number {
    return this.definition instanceof Line2D
      ? point.distanceToLine(this.definition)
      : point.distanceToLineSegment(this.definition);
  }

  distanceFromSquared(point: Vec2Struct): number {
    return this.definition instanceof Line2D
      ? vec2DistanceToLineSquared(point, this.definition)
      : vec2DistanceToSegmentSquared(point, this.definition);
  }

  getProjectedPoint(point: Point2D): Point2D | null {
    return this.definition instanceof Line2D
      ? point.projectOntoLine(this.definition)
      : point.projectOntoLineSegment(this.definition);
  }

  intersectWith(other: EntitySnapTarget): Point2D | null {
    if (this.definition instanceof LineSegment2D && other.definition instanceof LineSegment2D) {
      return this.definition.intersectWithSegment(other.definition);
    }

    if (this.definition instanceof Line2D) {
      return other.definition.intersectWithLine(this.definition);
    }

    if (other.definition instanceof Line2D) {
      return this.definition.intersectWithLine(other.definition);
    }

    return null;
  }

  toUserVisibleString(): string {
    switch (this.type) {
      case 'roof':
        return '<roof>';

      case 'ceiling':
        return '<ceiling>';

      case 'truss-envelope':
        return '<truss envelope>';

      case 'beam-bearing-envelope':
        return '<beam>';

      case 'structural-wall':
      case 'non-structural-wall':
        return '<wall>';

      case 'work-line':
        return '<work line>';

      case 'feature-pattern-container-edge':
      case 'insertion-feature-pattern-container-edge':
        return '<feature pattern container>';

      case 'uploaded-line':
        return '<uploaded line>';

      default:
        assertExhaustiveSwitch(this.type);
    }
  }

  toObject(): SerializedEntitySnapTarget {
    return {
      type: this.type,
      originObjectGuid: this.originObjectGuid,
      definition: this.definition.toObject(),
    };
  }
}
